import moment from 'moment';

/** Mirrors customer-service monitoringRoasCohortCount for UI display helpers. */
export function monitoringRoasCohortCount(pastCohortsToCheck, fundedCohortCount) {
  const lookback = Math.max(0, Number(pastCohortsToCheck) || 0);
  const funded = Math.max(0, Number(fundedCohortCount) || 0);
  // Deal lookback is the minimum; show max(lookback, funded). No default lookback.
  return Math.max(lookback, funded);
}

/** Formats API cohort key `YYYY-MM` as `July 2026`. */
export function formatMonitoringCohortLabel(cohortMonthKey) {
  if (!cohortMonthKey) {
    return null;
  }
  const parsed = moment(cohortMonthKey, ['YYYY-MM', moment.ISO_8601], true);
  if (!parsed.isValid()) {
    return String(cohortMonthKey);
  }
  return parsed.format('MMMM YYYY');
}

/** Formats closest repayment / exposure check date for the covenants header. */
export function formatMonitoringExposureDate(dateValue) {
  if (!dateValue) {
    return null;
  }
  const parsed = moment(dateValue, [moment.ISO_8601, 'YYYY-MM-DD'], true);
  if (!parsed.isValid()) {
    return String(dateValue);
  }
  return parsed.format('MMM D, YYYY');
}

/**
 * Covenants section title with cohort + exposure date context.
 * Example: `Covenants (Cohort= July 2026, Exposure date= Aug 1, 2026)`
 */
export function formatMonitoringCovenantsHeading({
  targetCohortMonth,
  closestRepaymentDate,
} = {}) {
  const cohortLabel = formatMonitoringCohortLabel(targetCohortMonth);
  const exposureDateLabel = formatMonitoringExposureDate(closestRepaymentDate);
  const parts = [];
  if (cohortLabel) {
    parts.push(`Cohort= ${cohortLabel}`);
  }
  if (exposureDateLabel) {
    parts.push(`Exposure date= ${exposureDateLabel}`);
  }
  if (parts.length === 0) {
    return 'Covenants';
  }
  return `Covenants (${parts.join(', ')})`;
}

/** Thousands separators in constraint text (e.g. Max $2000000 → Max $2,000,000). */
export function formatMonitoringConstraintValue(constraintText) {
  if (!constraintText) {
    return '—';
  }
  const shouldInterpretAsPercentDecimal = constraintText.includes('%');
  return String(constraintText).replace(/-?\d+(?:\.\d+)?/g, (numericPart) => {
    let numericValue = Number(numericPart);
    if (
      shouldInterpretAsPercentDecimal &&
      Number.isFinite(numericValue) &&
      Math.abs(numericValue) > 0 &&
      Math.abs(numericValue) < 1
    ) {
      numericValue *= 100;
    }
    if (!Number.isFinite(numericValue)) {
      return numericPart;
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numericValue);
  });
}

/** Drop Closest Repayment rows from older saved payloads. */
export function filterMonitoringCovenantRows(resultRows) {
  return (resultRows || []).filter((row) => row?.kpi !== 'Closest Repayment');
}

/** Normalize legacy covenant KPI labels for display. */
export function displayMonitoringCovenantKpi(kpi) {
  if (kpi === 'Funding % (MoM increase)' || kpi === 'Max MOM % increase') {
    return 'Max MoM % increase';
  }
  return kpi;
}

/** Tearsheet-style row divider between KPI rows (not after the last row). */
export function monitoringRowDividerProps(index, rowCount) {
  const showDivider = index < rowCount - 1;
  return {
    borderBottomWidth: showDivider ? '1px' : '0px',
    borderColor: 'gray.200',
  };
}

/** Resolved gross/net view for a ROAS cohort row (defaults to gross). */
export function monitoringRoasRowView(row) {
  const basis = row?.revenueBasis;
  return basis === 'net' ? 'net' : 'gross';
}

/** Display label for covenant basis and multiplier, e.g. Gross/100% or Net/60%. */
export function formatMonitoringRoasCovenantBasis(row) {
  const view = monitoringRoasRowView(row);
  const basisLabel = view === 'net' ? 'Net' : 'Gross';
  const mult = row?.covenantMultiplier;
  const pct =
    mult == null || Number.isNaN(Number(mult)) || Number(mult) <= 0
      ? 100
      : Number(mult) * 100;
  const pctText = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: pct % 1 === 0 ? 0 : 1,
  }).format(pct);
  return `${basisLabel}/${pctText}%`;
}

function monitoringRoasMeetsFlags(cell, view) {
  const isGross = view !== 'net';
  const meetsThreshold = isGross
    ? cell?.meetsThresholdGross
    : cell?.meetsThresholdNet;
  const meetsMom = isGross
    ? cell?.meetsMomGrowthGross
    : cell?.meetsMomGrowthNet;
  return { meetsThreshold, meetsMom };
}

/** Cell fails if min ROAS or MoM growth covenant fails. */
export function monitoringRoasCellFails(cell, view) {
  const { meetsThreshold, meetsMom } = monitoringRoasMeetsFlags(cell, view);
  if (meetsThreshold === false) {
    return true;
  }
  if (meetsMom === false) {
    return true;
  }
  return false;
}

export function monitoringRoasCellHasEvaluation(cell, view) {
  const { meetsThreshold, meetsMom } = monitoringRoasMeetsFlags(cell, view);
  return meetsThreshold != null || meetsMom != null;
}

export function monitoringRoasCellBackgroundColor(cell, view) {
  if (!monitoringRoasCellFails(cell, view)) {
    return undefined;
  }
  // Predicted covenant failures are amber; actual failures are deeper red. No green.
  return cell?.isPredicted ? 'orange.50' : 'red.200';
}

export function monitoringRoasCellValueColor(cell, view) {
  if (!monitoringRoasCellFails(cell, view)) {
    return undefined;
  }
  return cell?.isPredicted ? 'orange.700' : 'red.800';
}

export function formatMonitoringRoasCac(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
}

export function monitoringRoasCacFails(row) {
  return row?.meetsMaxCac === false;
}

/**
 * ROAS cohort aggregate for Monitoring KPI strip (7th covenant).
 * Fails only on actual (non-predicted) cell failures or CAC max fail.
 * Predicted-only cell fails do not count.
 */
export function monitoringRoasCohortActualPass(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) {
    return { evaluated: false, pass: true };
  }
  let evaluated = false;
  for (const row of list) {
    const view = monitoringRoasRowView(row);
    if (monitoringRoasCacFails(row)) {
      return { evaluated: true, pass: false };
    }
    if (row?.meetsMaxCac != null) {
      evaluated = true;
    }
    for (const cell of row?.cells || []) {
      if (!monitoringRoasCellHasEvaluation(cell, view)) {
        continue;
      }
      if (cell?.isPredicted) {
        continue;
      }
      evaluated = true;
      if (monitoringRoasCellFails(cell, view)) {
        return { evaluated: true, pass: false };
      }
    }
  }
  return { evaluated, pass: true };
}

/**
 * Covenant strip rollup: evaluated resultRows (pass/fail) + optional ROAS aggregate.
 * Returns { passed, total, allPass, failedNames }.
 */
export function monitoringCovenantStripSummary(resultRows, roasRows) {
  const rows = Array.isArray(resultRows) ? resultRows : [];
  let passed = 0;
  let total = 0;
  const failedNames = [];
  for (const row of rows) {
    if (row?.check !== 'pass' && row?.check !== 'fail') {
      continue;
    }
    total += 1;
    if (row.check === 'pass') {
      passed += 1;
    } else {
      failedNames.push(
        displayMonitoringCovenantKpi(row?.kpi) || 'Covenant',
      );
    }
  }
  const roas = monitoringRoasCohortActualPass(roasRows);
  if (roas.evaluated) {
    total += 1;
    if (roas.pass) {
      passed += 1;
    } else {
      failedNames.push('ROAS Cohort Performance');
    }
  }
  return {
    passed,
    total,
    allPass: total > 0 && passed === total,
    failedNames,
  };
}

export function monitoringKpiPctChange(current, prior) {
  if (current == null || prior == null) {
    return null;
  }
  const c = Number(current);
  const p = Number(prior);
  if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) {
    return null;
  }
  return ((c - p) / Math.abs(p)) * 100;
}

export function monitoringPctOfCap(value, cap) {
  if (value == null || cap == null) {
    return null;
  }
  const v = Number(value);
  const c = Number(cap);
  if (!Number.isFinite(v) || !Number.isFinite(c) || c === 0) {
    return null;
  }
  return (v / c) * 100;
}

export function monitoringRoasCacBackgroundColor(row) {
  return monitoringRoasCacFails(row) ? 'red.200' : undefined;
}

export function monitoringRoasCacValueColor(row) {
  return monitoringRoasCacFails(row) ? 'red.800' : undefined;
}

/** Covenant multiplier for a row (defaults to 1.0 = 100%). */
export function monitoringRoasCovenantMultiplier(covenantMultiplier) {
  if (
    covenantMultiplier == null ||
    Number.isNaN(Number(covenantMultiplier)) ||
    Number(covenantMultiplier) <= 0
  ) {
    return 1;
  }
  return Number(covenantMultiplier);
}

export function monitoringRoasRawPct(cell, view) {
  const raw = view === 'net' ? cell?.netRoasPct : cell?.grossRoasPct;
  if (raw == null || Number.isNaN(Number(raw))) {
    return null;
  }
  return Number(raw);
}

/** ROAS pct on covenant basis (raw gross/net × row multiplier). */
export function monitoringRoasCovenantScaledPct(cell, view, covenantMultiplier = 1) {
  const raw = monitoringRoasRawPct(cell, view);
  if (raw == null) {
    return null;
  }
  return raw * monitoringRoasCovenantMultiplier(covenantMultiplier);
}

export function formatMonitoringRoasCellValue(cell, view, covenantMultiplier = 1) {
  const scaled = monitoringRoasCovenantScaledPct(cell, view, covenantMultiplier);
  if (scaled == null || Number.isNaN(scaled)) {
    return '—';
  }
  return `${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(scaled * 100)}%`;
}

/** Format a ROAS cell using the row's resolved view and covenant multiplier. */
export function formatMonitoringRoasRowCellValue(cell, row) {
  return formatMonitoringRoasCellValue(
    cell,
    monitoringRoasRowView(row),
    row?.covenantMultiplier,
  );
}

export function monitoringRoasCellTooltip(cell, view) {
  if (view === 'net') {
    return cell?.issueTooltipNet || null;
  }
  return cell?.issueTooltipGross || null;
}

function parseMonitoringRoasDate(value) {
  if (!value) {
    return null;
  }
  if (typeof value === 'object' && value.year != null && value.month != null) {
    const parsed = moment({
      year: Number(value.year),
      month: Number(value.month) - 1,
      day: Number(value.day) || 1,
    });
    return parsed.isValid() ? parsed : null;
  }
  const parsed = moment(
    value,
    [moment.ISO_8601, 'YYYY-MM-DD', 'YYYY-MM', 'MMMM YYYY', 'MMM YYYY'],
    true,
  );
  return parsed.isValid() ? parsed : null;
}

/** Months between cohort start and as-of month (0-based MOB). */
export function monitoringRoasPeriodForAsOf(cohortDate, asOfDate) {
  const cohort = parseMonitoringRoasDate(cohortDate);
  const asOf = parseMonitoringRoasDate(asOfDate);
  if (!cohort || !asOf) {
    return null;
  }
  const months =
    (asOf.year() - cohort.year()) * 12 + (asOf.month() - cohort.month());
  if (months < 0) {
    return null;
  }
  return months;
}

/**
 * Quick view: previous calendar month + current (as-of) month cells for a cohort,
 * plus aggregated pass/fail and failure reasons (incl. CAC vs max CAC).
 */
export function buildMonitoringRoasQuickViewRow(row, asOfDate) {
  const grossNetView = monitoringRoasRowView(row);
  const cells = row?.cells || [];
  const currentPeriod = monitoringRoasPeriodForAsOf(
    row?.cohortDate || row?.cohortLabel,
    asOfDate,
  );
  const previousPeriod =
    currentPeriod == null || currentPeriod < 1 ? null : currentPeriod - 1;

  const cellByPeriod = new Map(
    cells.map((cell) => [Number(cell.period), cell]),
  );
  const previousCell =
    previousPeriod == null ? null : cellByPeriod.get(previousPeriod) || null;
  const currentCell =
    currentPeriod == null ? null : cellByPeriod.get(currentPeriod) || null;

  const reasons = [];
  const checked = [previousCell, currentCell].filter(Boolean);
  let failed = false;

  if (monitoringRoasCacFails(row)) {
    failed = true;
    reasons.push(row?.cacIssueTooltip || 'Above max CAC');
  }

  for (const cell of checked) {
    if (!monitoringRoasCellFails(cell, grossNetView)) {
      continue;
    }
    failed = true;
    const tip = monitoringRoasCellTooltip(cell, grossNetView);
    if (tip) {
      const label = cell.isPredicted
        ? `P${cell.period} (predicted)`
        : `P${cell.period}`;
      reasons.push(`${label}: ${tip}`);
    }
  }

  const hasCacEval = row?.meetsMaxCac != null;
  const hasAnyEval = checked.length > 0 || hasCacEval;

  return {
    previousPeriod,
    currentPeriod,
    previousCell,
    currentCell,
    status: !hasAnyEval ? 'none' : failed ? 'fail' : 'pass',
    reason: reasons.join(' · ') || null,
  };
}

/**
 * List URL for Monitoring. When already on the list route (no monitoringId),
 * callers must reset local create-mode state — navigate alone is a no-op.
 */
export function monitoringListPath(customerId) {
  return `/customers/${customerId}/performance/monitoring`;
}

/** True when Back to list must navigate (saved report URL). */
export function monitoringBackToListNeedsNavigate(monitoringId) {
  return Boolean(monitoringId);
}
